import { useState } from 'react';
import {
  MessageSquare,
  Loader2,
  AlertCircle,
  Star,
  Check,
  EyeOff,
} from 'lucide-react';
import { ReviewStatus } from '@pms/shared';
import {
  usePlatformGuestReviews,
  useApproveGuestReview,
  useRejectGuestReview,
} from '@/hooks/api/usePlatform';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

const STATUS_TABS: { value: ReviewStatus; label: string }[] = [
  { value: ReviewStatus.PENDING, label: 'Pending' },
  { value: ReviewStatus.APPROVED, label: 'Approved' },
  { value: ReviewStatus.HIDDEN, label: 'Hidden' },
];

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function Stars({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating} out of 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={14}
          className={i < rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
          aria-hidden="true"
        />
      ))}
    </span>
  );
}

/**
 * Cross-tenant guest-review moderation queue for the platform (super-admin)
 * console. Lists post-stay guest reviews across all operators, filtered by
 * status, via GET /v1/admin/platform/guest-reviews.
 *
 * Moderation: each row exposes the actions its status allows —
 *   - PENDING  → Approve (publish) + Hide (never show)
 *   - APPROVED → Hide
 *   - HIDDEN   → Approve
 * Approve POSTs .../guest-reviews/:id/approve (→ APPROVED, shows on the site);
 * Hide POSTs .../guest-reviews/:id/reject (→ HIDDEN, won't show on the site).
 */
export function AdminGuestReviewsPage() {
  const [status, setStatus] = useState<ReviewStatus>(ReviewStatus.PENDING);
  const { data, isLoading, isError, refetch } = usePlatformGuestReviews(status);

  const approve = useApproveGuestReview();
  const reject = useRejectGuestReview();
  const pendingId =
    (approve.isPending ? (approve.variables as string) : undefined) ??
    (reject.isPending ? (reject.variables as string) : undefined);
  const errorId =
    approve.isError && !approve.isPending
      ? (approve.variables as string)
      : reject.isError && !reject.isPending
        ? (reject.variables as string)
        : undefined;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Guest Reviews"
        description="Post-stay guest reviews across all operators."
      />

      {/* Status tabs */}
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Review status filter">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={status === tab.value}
            onClick={() => setStatus(tab.value)}
            className={cn(
              'px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
              status === tab.value
                ? 'bg-brand-600 text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-brand-600" aria-hidden="true" />
            <span className="ml-2 text-sm text-gray-500">Loading reviews…</span>
          </div>
        ) : isError ? (
          <div className="py-16 text-center">
            <AlertCircle size={24} className="mx-auto text-red-500 mb-3" aria-hidden="true" />
            <p className="text-sm text-red-700 mb-4">Unable to load reviews.</p>
            <Button variant="secondary" onClick={() => void refetch()}>Retry</Button>
          </div>
        ) : !data || data.length === 0 ? (
          <EmptyState
            icon={MessageSquare}
            title="Nothing here"
            description={`No ${STATUS_TABS.find((t) => t.value === status)?.label.toLowerCase()} reviews.`}
          />
        ) : (
          <ul className="divide-y divide-gray-100">
            {data.map((r) => {
              const canApprove = r.status !== ReviewStatus.APPROVED;
              const canHide = r.status !== ReviewStatus.HIDDEN;
              const rowBusy = pendingId === r.id;
              return (
                <li key={r.id} className="px-5 py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Stars rating={r.rating} />
                        {r.title && <span className="text-sm font-semibold text-gray-900 truncate">{r.title}</span>}
                      </div>
                      {r.body && <p className="mt-1.5 text-sm text-gray-600 whitespace-pre-wrap">{r.body}</p>}
                      <p className="mt-1.5 text-xs text-gray-400">
                        {r.guestName} · {r.propertyName}
                        {r.operatorName ? ` · ${r.operatorName}` : ''} · {formatDate(r.createdAt)}
                      </p>
                    </div>

                    {/* Moderation actions — offered per the row's current status. */}
                    <div className="flex shrink-0 items-center gap-2">
                      {canApprove && (
                        <button
                          type="button"
                          onClick={() => approve.mutate(r.id)}
                          disabled={rowBusy}
                          title="Approve — this review will show on the property page."
                          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:opacity-60"
                        >
                          {rowBusy && approve.variables === r.id ? (
                            <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                          ) : (
                            <Check size={14} aria-hidden="true" />
                          )}
                          Approve
                        </button>
                      )}
                      {canHide && (
                        <button
                          type="button"
                          onClick={() => reject.mutate(r.id)}
                          disabled={rowBusy}
                          title="Hide — this review won't show on the property page."
                          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-60"
                        >
                          {rowBusy && reject.variables === r.id ? (
                            <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                          ) : (
                            <EyeOff size={14} aria-hidden="true" />
                          )}
                          Hide
                        </button>
                      )}
                    </div>
                  </div>

                  {errorId === r.id && (
                    <p role="alert" className="mt-2 text-xs font-medium text-red-600">
                      Couldn't update this review. Please try again.
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
